import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { MessageCircle, Calendar } from 'lucide-react';

interface ConnectionAcceptedModalProps {
  connectionId: string;
  otherUserId: string;
  otherUsername: string;
  otherUserPhoto?: string;
  currentUserId: string;
  currentUsername: string;
  onClose: () => void;
  onStartChat: () => void;
  onStartPlanning: () => void;
}

interface UIState {
  mode: 'idle' | 'planning' | 'chatting';
  actorUserId?: string;
  actorUsername?: string;
  updatedAt?: any;
}

const ConnectionAcceptedModal: React.FC<ConnectionAcceptedModalProps> = ({
  connectionId,
  otherUserId,
  otherUsername,
  otherUserPhoto: otherUserPhotoProp,
  currentUserId,
  currentUsername,
  onClose,
  onStartChat,
  onStartPlanning,
}) => {
  const [uiState, setUIState] = useState<UIState>({ mode: 'idle' });
  const [inFlight, setInFlight] = useState(false);
  const [otherUserPhoto, setOtherUserPhoto] = useState<string | undefined>(otherUserPhotoProp);
  const [otherUserPhotoPosition, setOtherUserPhotoPosition] = useState<{ x: number; y: number } | null>(null);

  // Fetch other user's photo + crop position
  useEffect(() => {
    if (!otherUserId) return;

    const fetchUserProfile = async () => {
      try {
        const snap = await getDoc(doc(db, 'userProfiles', otherUserId));
        if (snap.exists()) {
          const d = snap.data();
          if (!otherUserPhotoProp) {
            setOtherUserPhoto(d?.profilePhoto || d?.photoURL);
          }
          if (d?.photoPosition) {
            setOtherUserPhotoPosition({ x: d.photoPosition.x ?? 50, y: d.photoPosition.y ?? 50 });
          }
        }
      } catch {
        // silently ignore
      }
    };

    fetchUserProfile();
  }, [otherUserId, otherUserPhotoProp]);

  // Realtime listener for UI state
  useEffect(() => {
    if (!connectionId) return;

    const connectionRef = doc(db, 'connections', connectionId);
    
    const unsubscribe = onSnapshot(connectionRef, (snapshot) => {
      if (!snapshot.exists()) return;
      
      const data = snapshot.data();
      const state = data.uiState || {};
      
      setUIState({
        mode: state.mode || 'idle',
        actorUserId: state.actorUserId,
        actorUsername: state.actorUsername,
        updatedAt: state.updatedAt,
      });
    }, (error) => {
      console.error('Error listening to connection UI state:', error);
    });

    return () => unsubscribe();
  }, [connectionId]);

  // Derive whether other user is acting
  const isOtherActing = !!(uiState.actorUserId && uiState.actorUserId !== currentUserId);
  const isPlanningLocked = uiState.mode === 'planning' && isOtherActing;
  const isChattingLocked = uiState.mode === 'chatting' && isOtherActing;

  const handleStartChat = async () => {
    if (inFlight) return;
    setInFlight(true);
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        'uiState.mode': 'chatting',
        'uiState.actorUserId': currentUserId,
        'uiState.actorUsername': currentUsername,
        'uiState.updatedAt': serverTimestamp(),
      });
      onStartChat();
    } catch (err) {
      console.error('Failed to update connection UI state:', err);
      setInFlight(false);
    }
  };

  const handleStartPlanning = async () => {
    if (inFlight || isPlanningLocked) return;
    
    setInFlight(true);
    try {
      // Update Firestore state
      await updateDoc(doc(db, 'connections', connectionId), {
        'uiState.mode': 'planning',
        'uiState.actorUserId': currentUserId,
        'uiState.actorUsername': currentUsername,
        'uiState.updatedAt': serverTimestamp(),
      });
      
      // Navigate to planning
      onStartPlanning();
    } catch (err) {
      console.error('Failed to update connection UI state:', err);
      setInFlight(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <style>
        {`
          @keyframes pulseOrangeGray {
            0%, 100% {
              background-color: #4F6660;
              color: white;
              box-shadow: 0 0 0 0 rgba(255, 107, 53, 0.7);
            }
            50% {
              background-color: #E5E7EB;
              color: #6B7280;
              box-shadow: 0 0 0 8px rgba(255, 107, 53, 0);
            }
          }
          .animate-pulse-orange-gray {
            animation: pulseOrangeGray 1.5s ease-in-out infinite;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4"
        // Backdrop click disabled to prevent accidental close
      >
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#8DA8A0' }}>
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Verbinding geaccepteerd
            </h3>
            
            {/* User info */}
            <div className="flex items-center justify-center gap-3 mb-4">
              {otherUserPhoto ? (
                <img 
                  src={otherUserPhoto} 
                  alt={otherUsername} 
                  className="w-12 h-12 rounded-full object-cover shadow-sm"
                  style={{
                    border: '2px solid #8DA8A0',
                    objectPosition: otherUserPhotoPosition
                      ? `${otherUserPhotoPosition.x}% ${otherUserPhotoPosition.y}%`
                      : 'center',
                  }}
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextElementSibling) {
                      (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              {!otherUserPhoto && (
                <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: '#8DA8A0' }}>
                  <span className="text-white font-bold text-base">
                    {otherUsername.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {/* Hidden fallback for image error */}
              {otherUserPhoto && (
                <div className="w-12 h-12 rounded-full items-center justify-center shadow-sm" style={{ backgroundColor: '#8DA8A0', display: 'none' }}>
                  <span className="text-white font-bold text-base">
                    {otherUsername.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <p className="text-sm text-gray-700">
                <strong className="text-gray-900">{otherUsername}</strong> is nu verbonden
              </p>
            </div>
            
            <p className="text-sm text-gray-500">
              Wil je nu chatten, of eerst een moment inplannen?
            </p>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            {/* Start Chat Button — solid sage */}
            <button
              onClick={handleStartChat}
              disabled={Boolean(inFlight)}
              style={{
                backgroundColor: isChattingLocked ? undefined : inFlight ? '#A8C4BC' : '#8DA8A0',
                color: 'white',
              }}
              className={`w-full py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                isChattingLocked
                  ? 'animate-pulse-orange-gray'
                  : inFlight
                  ? 'opacity-60 cursor-wait'
                  : ''
              }`}
              onMouseEnter={e => { if (!isChattingLocked && !inFlight) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#7A9488'; }}
              onMouseLeave={e => { if (!isChattingLocked && !inFlight) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#8DA8A0'; }}
            >
              <MessageCircle size={16} strokeWidth={2} />
              {isChattingLocked ? 'Join chat!' : 'Start chat'}
            </button>

            {/* Inplannen Button — sage outline */}
            <button
              onClick={handleStartPlanning}
              disabled={Boolean(inFlight || isChattingLocked)}
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #8DA8A0',
                color: isChattingLocked ? '#C4C4C4' : '#3C4F49',
              }}
              className={`w-full py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                isChattingLocked
                  ? 'opacity-40 cursor-not-allowed'
                  : inFlight
                  ? 'opacity-60 cursor-wait'
                  : ''
              }`}
              onMouseEnter={e => { if (!isChattingLocked && !inFlight) { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#8DA8A0'; (e.currentTarget as HTMLButtonElement).style.color = 'white'; } }}
              onMouseLeave={e => { if (!isChattingLocked && !inFlight) { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#3C4F49'; } }}
            >
              <Calendar size={16} strokeWidth={2} />
              {isChattingLocked ? `${uiState.actorUsername} is in chat` : 'Inplannen'}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="w-full text-[#9BA8A3] py-2 rounded-lg hover:text-[#3C4F49] transition-colors text-sm font-medium"
            >
              Sluit af
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ConnectionAcceptedModal;

